import { useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Leaf, HeartPulse, Sprout } from "lucide-react";

const MISSIONS = [
  {
    key: "mission-1",
    label: "Mission 01",
    title: "Community upliftment, irrespective of caste, creed or colour.",
    icon: Leaf,
    points: [
      "Work for the upliftment of the community irrespective of caste, creed and colour.",
      "Plantation of medicinal plants — Jatropha (Ratnajot) to produce bio-diesel, Ayurveda and Homeopathic medicine — to uplift rural men and women by generating employment and treating AIDS, Cancer, Drug-addiction, Malaria, Kala-azar, Silicosis and other killer diseases.",
      "Provide legal support to citizens and run a Nation-wide Literacy Programme, especially for weaker sections of men and women.",
      "Help construct houses for low-income groups under State and Central Government Yojnas.",
      "Promote science, culture, art, literature and charitable activity through a not-for-profit registered society.",
    ],
  },
  {
    key: "mission-2",
    label: "Mission 02",
    title: "Health, education and an inclusive social fabric.",
    icon: HeartPulse,
    points: [
      "Organize and promote health and HIV / AIDS awareness programmes.",
      "Develop human resources in rural and backward areas — schools, books, scholarships and learning facilities.",
      "Work for a green and clean environment, disaster management and animal care.",
      "Establish medical and technical institutions, libraries, orphanages and old-age homes.",
      "Promote women through SHGs, women & child development programmes, legal awareness, non-formal education, vocational training centres, seminars, rallies and skill-development centres.",
    ],
  },
  {
    key: "mission-3",
    label: "Mission 03",
    title: "Sustainable livelihoods and farmer empowerment.",
    icon: Sprout,
    points: [
      "Enhance production through Natural Resource Management without disturbing ecological balance.",
      "Empower farmers through collectives.",
      "Supplement existing school curriculum with value-based inputs.",
      "Improve health conditions, with special focus on rural and urban-slum women and the girl child.",
      "Support sustainable livelihood activities to increase family income.",
      "Transfer technology — Lab to Land — and train farmers on profitable, non-chemical agriculture.",
      "Build basic awareness of the laws of the land to empower citizens on legal issues.",
      "Expansion approved for Odisha, Andhra Pradesh, Telangana and Chhattisgarh by the Government of Registrations, Ranchi.",
      "NABARD support is being mobilized for implementation reflecting in 2026.",
    ],
  },
];

export default function About() {
  const [tab, setTab] = useState(MISSIONS[0].key);
  const active = MISSIONS.find((m) => m.key === tab) || MISSIONS[0];

  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-20 md:py-32 bg-cream"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Intro */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          {/* Left image stack */}
          <div className="md:col-span-5 relative">
            <div className="aspect-[3/4] overflow-hidden bg-line">
              <img
                src="/imgs/indian-volunteers-action.jpg"
                alt="Community members gathered for a ward meeting"
                className="w-full h-full object-cover"
                data-testid="about-image-primary"
              />
            </div>
            <div className="hidden md:block absolute -bottom-10 -right-10 w-44 h-56 overflow-hidden border-8 border-cream">
              <img
                src="/imgs/indian-children-classroom.jpg"
                alt="Volunteers"
                className="w-full h-full object-cover"
                data-testid="about-image-secondary"
              />
            </div>
          </div>

          {/* Right copy */}
          <div className="md:col-span-7 md:pl-12">
            <p className="overline mb-6">Who we are</p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl text-ink leading-tight text-balance">
              We don't build <em className="italic text-terracotta">for</em> communities. We build <em className="italic text-forest">with</em> them.
            </h2>

            <div className="mt-8 space-y-5 text-graphite md:text-lg leading-relaxed">
              <p>
                Jharkhand Rastriya Sarva Dharma Welfare Association was
                founded in 2007 in Jamshedpur, Jharkhand — Steel City — to
                serve the poor, uplift women, educate children, and stand with
                communities regardless of background. Eighteen years on, we
                continue that work across five states.
              </p>
              <p>
                We work at the intersection of livelihoods, health, education
                and civic empowerment — because urban and rural poverty isn't a
                single problem, and there are no single-issue solutions.
              </p>
              <p>
                Beyond our home state of Jharkhand, we are now formally{" "}
                <strong className="text-ink">authorised by the Government of Registrations, Ranchi</strong>{" "}
                to extend our development work and welfare activities across{" "}
                <strong className="text-ink">Odisha, Andhra Pradesh, Telangana and Chhattisgarh</strong>{" "}
                — a mandate that lets us carry the same community-led approach
                to the people who need it most in four additional states.
              </p>
            </div>

            <div
              className="mt-8 bg-sand border border-line p-5 flex flex-wrap items-center gap-4"
              data-testid="authorised-states"
            >
              <p className="overline text-terracotta">Authorised in</p>
              {["Jharkhand", "Odisha", "Andhra Pradesh", "Telangana", "Chhattisgarh"].map((s) => (
                <span
                  key={s}
                  className="inline-flex items-center gap-2 text-ink text-sm border border-line bg-cream px-3 py-1.5"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-mustard" />
                  {s}
                </span>
              ))}
            </div>

            <div className="mt-10 grid grid-cols-2 gap-6">
              <div className="border-l-2 border-terracotta pl-5">
                <p className="font-serif text-3xl text-ink leading-none">62</p>
                <p className="overline mt-2">Wards across 7 states</p>
              </div>
              <div className="border-l-2 border-forest pl-5">
                <p className="font-serif text-3xl text-ink leading-none">94%</p>
                <p className="overline mt-2">Funds to programs</p>
              </div>
            </div>
          </div>
        </div>

        {/* ───── Our Mission ───── */}
        <div className="mt-24 md:mt-36" data-testid="mission-block">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-10">
            <div className="md:col-span-7">
              <p className="overline mb-6">Our mission</p>
              <h3 className="text-3xl md:text-5xl text-ink leading-tight text-balance">
                Three streams of <em className="italic text-terracotta">work</em>, one shared purpose.
              </h3>
            </div>
            <p className="md:col-span-4 md:col-start-9 text-graphite leading-relaxed">
              Our mission has matured over eighteen years across three overlapping streams. Each operates independently — together they make a community whole.
            </p>
          </div>

          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList
              data-testid="mission-tabs"
              className="bg-transparent p-0 h-auto flex flex-wrap gap-2 mb-8 justify-start"
            >
              {MISSIONS.map((m) => {
                const Icon = m.icon;
                return (
                  <TabsTrigger
                    key={m.key}
                    value={m.key}
                    data-testid={`mission-tab-${m.key}`}
                    className="rounded-none border border-line bg-cream text-graphite data-[state=active]:bg-forest data-[state=active]:text-cream data-[state=active]:border-forest px-5 py-3 font-sans text-sm flex items-center gap-2 transition-colors"
                  >
                    <Icon size={16} strokeWidth={1.5} />
                    {m.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {MISSIONS.map((m) => (
              <TabsContent
                key={m.key}
                value={m.key}
                data-testid={`mission-content-${m.key}`}
                className="mt-0"
              >
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 bg-sand border border-line p-7 md:p-12">
                  <div className="md:col-span-5">
                    <div className="w-12 h-12 bg-mustard text-ink flex items-center justify-center mb-6">
                      <m.icon size={20} strokeWidth={1.5} />
                    </div>
                    <p className="overline mb-3 text-terracotta">
                      {m.label}
                    </p>
                    <h4 className="font-serif text-3xl md:text-4xl text-ink leading-tight text-balance">
                      {m.title}
                    </h4>
                  </div>
                  <ul className="md:col-span-7 space-y-5">
                    {m.points.map((p, i) => (
                      <li
                        key={i}
                        className="flex gap-4 text-graphite leading-relaxed"
                      >
                        <span className="font-serif text-terracotta text-xl leading-none mt-1 flex-shrink-0">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </div>
    </section>
  );
}
